import { useState, type FormEvent } from "react";
import { signIn, type AdminSession } from "../../services/adminSession";
import { useI18n } from "../../i18n/localization";
import { signInErrorMessage } from "./authMessages";

// Administrator sign-in form for the protected /admin and /schedule screens. The password is
// held only in this component's state while typing and is cleared after every attempt.
export function AdminLogin({ notice, onSignedIn }: { notice: string | null; onSignedIn: (s: AdminSession) => void }) {
  const { t, dir } = useI18n();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError(t.auth.required);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await signIn(username.trim(), password));
    } catch (err) {
      setPassword("");
      setError(signInErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main dir={dir} style={{ padding: 24, fontFamily: "Segoe UI, Arial, sans-serif" }}>
      <form onSubmit={submit} aria-labelledby="admin-login-title" noValidate
        style={{ border: "1px solid #d6deea", borderRadius: 10, background: "#fff", padding: 22, maxWidth: 400, margin: "24px auto" }}>
        <h1 id="admin-login-title" style={{ color: "#14305f", fontSize: 18, margin: "0 0 8px" }}>{t.auth.title}</h1>
        <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 16px" }}>{t.auth.hint}</p>
        {notice && !error && (
          <div role="status" style={{ color: "#b45309", fontSize: 13, marginBottom: 12 }}>{notice}</div>
        )}
        <label style={label}>
          {t.auth.username}
          <input value={username} onChange={(e) => setUsername(e.target.value)} name="username"
            autoComplete="username" autoCapitalize="none" spellCheck={false} dir="ltr" required autoFocus
            aria-invalid={!!error} style={input} />
        </label>
        <label style={label}>
          {t.auth.password}
          <input value={password} onChange={(e) => setPassword(e.target.value)} name="password" type="password"
            autoComplete="current-password" dir="ltr" required aria-invalid={!!error} style={input} />
        </label>
        <div aria-live="assertive" style={{ minHeight: 18, marginBottom: 10 }}>
          {error && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{error}</div>}
        </div>
        <button type="submit" disabled={busy} aria-busy={busy}
          style={{ background: busy ? "#94a3b8" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "8px 20px", cursor: busy ? "default" : "pointer" }}>
          {busy ? t.auth.checking : t.auth.submit}
        </button>
      </form>
    </main>
  );
}

const label: React.CSSProperties = { display: "block", color: "#14305f", fontSize: 13, marginBottom: 12 };
const input: React.CSSProperties = {
  display: "block", width: "100%", marginTop: 4, padding: "8px 10px",
  border: "1px solid #d6deea", borderRadius: 6, boxSizing: "border-box"
};
